import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import '../styles/FormattedContentDisplay.css';
import { getStringValue } from '../utils/blockHelpers';
import TeamComparisonDisplay from './TeamComparisonDisplay';
import MaxPrepsDisplay from './MaxPrepsDisplay';

const sanitizeStyle = (styleStr) => {
  if (!styleStr) return '';
  
  const allowedProps = [
    'color', 'background-color', 'background',
    'font-size', 'font-weight', 'font-style',
    'text-decoration', 'text-align',
    'margin', 'padding',
    'display', 'line-height'
  ];
  
  const styles = styleStr.split(';').filter(s => s.trim());
  const filtered = styles
    .filter(style => {
      const prop = style.split(':')[0].trim().toLowerCase();
      return allowedProps.some(allowed => allowed === prop);
    })
    .join(';');
  
  return filtered;
};

const renderBlock = (block) => {
  switch (block.type) {
    case 'text':
      const level = block.formatting?.level || 'p';
      const formatting = block.formatting || {};
      const baseStyle = {
        fontFamily: formatting.fontFamily || 'sans-serif',
      };

      if (formatting.listType === 'ordered') {
        const lines = (block.content || '').split('\n');
        return (
          <ol key={block.id} style={baseStyle} className="content-list">
            {lines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ol>
        );
      } else if (formatting.listType === 'bullet') {
        const lines = (block.content || '').split('\n');
        return (
          <ul key={block.id} style={baseStyle} className="content-list">
            {lines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        );
      }

      let content = block.content;
      let className = '';
      
      if (block.segments && block.segments.length > 0) {
        content = (
          <>
            {block.segments.map((segment, idx) => {
              const segmentStyle = {
                fontWeight: segment.formatting.bold ? 'bold' : 'normal',
                fontStyle: segment.formatting.italic ? 'italic' : 'normal',
                color: segment.formatting.color || 'inherit',
              };

              if (segment.formatting.underline && segment.formatting.strikethrough) {
                segmentStyle.textDecoration = 'underline line-through';
              } else if (segment.formatting.underline) {
                segmentStyle.textDecoration = 'underline';
              } else if (segment.formatting.strikethrough) {
                segmentStyle.textDecoration = 'line-through';
              }

              return (
                <span key={idx} style={segmentStyle}>
                  {segment.text}
                </span>
              );
            })}
          </>
        );
      }

      const checkIndent = () => {
        if (level !== 'p') return false;
        if (block.segments && block.segments.length > 0 && block.segments[0].text.startsWith('    ')) {
          return true;
        }
        if (block.content && block.content.startsWith('    ')) {
          return true;
        }
        return false;
      };

      if (checkIndent()) {
        className = 'indent';
      }

      return React.createElement(level, { key: block.id, style: baseStyle, className }, content);

    case 'image':
      return (
        <div key={block.id} className="content-image-block">
          {block.src ? (
            <>
              <img src={block.src} alt={block.alt || 'Article image'} />
              {block.caption && <p className="image-caption">{block.caption}</p>}
            </>
          ) : null}
        </div>
      );
    case 'video':
      return (
        <div key={block.id} className="content-video-block">
          {block.url ? (
            <>
              {block.isLocalFile ? (
                <video width="100%" controls src={block.url} title={block.title || 'Video'} />
              ) : block.url.includes('youtube') || block.url.includes('youtu.be') ? (
                <iframe
                  width="100%"
                  height="400"
                  src={`https://www.youtube.com/embed/${block.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^\s&]+)/)?.[1]}`}
                  title={block.title || 'Video'}
                  frameBorder="0"
                  allowFullScreen
                />
              ) : (
                <video width="100%" controls src={block.url} title={block.title || 'Video'} />
              )}
              {block.title && <p className="video-title">{block.title}</p>}
            </>
          ) : null}
        </div>
      );
    case 'player-comparison':
      const statCategories = ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'BLOCKING', 'KICKING'];
      const p1Stats = block.player1_stats || {};
      const p2Stats = block.player2_stats || {};
      
      return (
        <div key={block.id} className="content-player-comparison">
          <div className="comparison-header">
            <div className="comparison-player">
              <h4>{getStringValue(block.player1_name, 'Player 1')}</h4>
              {block.player1_team && <p className="player-team">{getStringValue(block.player1_team)}</p>}
            </div>
            <div className="comparison-vs">VS</div>
            <div className="comparison-player">
              <h4>{getStringValue(block.player2_name, 'Player 2')}</h4>
              {block.player2_team && <p className="player-team">{getStringValue(block.player2_team)}</p>}
            </div>
          </div>
          
          {Object.keys(p1Stats).length > 0 || Object.keys(p2Stats).length > 0 ? (
            <div className="comparison-stats-container">
              {statCategories.map(category => {
                const p1Data = p1Stats[category];
                const p2Data = p2Stats[category];
                
                if (!p1Data && !p2Data) return null;
                
                const getStatKeys = () => {
                  if (p1Data) return Object.keys(p1Data).filter(k => k !== 'NAME' && k !== 'TEAM' && k !== 'ID' && k !== 'POS');
                  if (p2Data) return Object.keys(p2Data).filter(k => k !== 'NAME' && k !== 'TEAM' && k !== 'ID' && k !== 'POS');
                  return [];
                };
                
                const statKeys = getStatKeys();
                
                return (
                  <div key={category} className="comparison-category">
                    <h5>{category}</h5>
                    <table>
                      <thead>
                        <tr>
                          <th>Statistic</th>
                          <th>{getStringValue(block.player1_name)}</th>
                          <th>{getStringValue(block.player2_name)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statKeys.map((key) => {
                          const v1 = p1Data?.[key];
                          const v2 = p2Data?.[key];
                          const n1 = parseFloat(v1);
                          const n2 = parseFloat(v2);
                          let comparison = 0;
                          if (!isNaN(n1) && !isNaN(n2)) {
                            comparison = n1 > n2 ? 1 : n1 < n2 ? -1 : 0;
                          }
                          
                          const formatValue = (val) => {
                            if (val === null || val === undefined) return '-';
                            if (typeof val === 'number') return val.toFixed(2);
                            return val;
                          };
                          
                          return (
                            <tr key={key}>
                              <td className="stat-name">{key.replace(/_/g, ' ')}</td>
                              <td className={`stat-value ${comparison > 0 ? 'winner' : ''}`}>
                                {formatValue(v1)}
                              </td>
                              <td className={`stat-value ${comparison < 0 ? 'winner' : ''}`}>
                                {formatValue(v2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="comparison-display">
              <div className="player-card">
                <h4>{getStringValue(block.player1_name, 'Player 1')}</h4>
                {block.player1_team && <p>{getStringValue(block.player1_team)}</p>}
              </div>
              <div className="comparison-vs">VS</div>
              <div className="player-card">
                <h4>{getStringValue(block.player2_name, 'Player 2')}</h4>
                {block.player2_team && <p>{getStringValue(block.player2_team)}</p>}
              </div>
            </div>
          )}
        </div>
      );
    case 'maxpreps':
      return (
        <div key={block.id} className="maxpreps-display">
          <MaxPrepsDisplay block={block} />
        </div>
      );

    case 'separator':
      return <hr key={block.id} className="content-separator" />;

    case 'teamcomparison':
      return (
        <div key={block.id} className="content-team-comparison">
          <TeamComparisonDisplay block={block} />
        </div>
      );

    case 'whatsnext':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>🏃 What's Next</h4>
          <div style={{ fontSize: '0.95rem' }}>
            {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
            {block.opponent && <div><strong>Opponent:</strong> {getStringValue(block.opponent)}</div>}
            {block.date && <div><strong>Date:</strong> {getStringValue(block.date)}</div>}
            {block.details && <div><strong>Details:</strong> {getStringValue(block.details)}</div>}
          </div>
        </div>
      );

    case 'leaderboard':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>📈 Leaderboard Entry</h4>
          <div style={{ fontSize: '0.95rem' }}>
            {block.player && <div><strong>Player:</strong> {getStringValue(block.player)}</div>}
            {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
            {block.stat && <div><strong>Stat:</strong> {getStringValue(block.stat)}</div>}
            {block.value && <div><strong>Value:</strong> {getStringValue(block.value)}</div>}
          </div>
        </div>
      );

    case '247sports':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>⭐ 247Sports</h4>
          <div style={{ fontSize: '0.95rem' }}>
            <div><strong>Type:</strong> {block.type === 'player' ? 'Player' : 'Team'}</div>
            {block.name && <div><strong>Name:</strong> {getStringValue(block.name)}</div>}
          </div>
        </div>
      );

    case 'xfactor':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>✨ X-Factor</h4>
          <div style={{ fontSize: '0.95rem' }}>
            {block.player && <div><strong>Player:</strong> {getStringValue(block.player)}</div>}
            {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
            {block.analysis && <div><strong>Analysis:</strong> {getStringValue(block.analysis)}</div>}
          </div>
        </div>
      );

    case 'hottakes':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: `2px solid ${block.heat === 'Volcanic' ? '#ff4444' : block.heat === 'Spicy' ? '#ff9944' : block.heat === 'Medium' ? '#ffbb33' : '#88ddff'}`, borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>🔥 Hot Takes - {block.heat || 'Medium'}</h4>
          {block.content && <p style={{ margin: '0', fontSize: '0.95rem' }}>{block.content}</p>}
        </div>
      );

    case 'powerranking':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>👑 Power Ranking</h4>
          <div style={{ fontSize: '0.95rem' }}>
            <div><strong>Rank:</strong> #{getStringValue(block.rank, 'N/A')}</div>
            {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
          </div>
        </div>
      );

    case 'momentummeter':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>📊 Momentum Meter</h4>
          <div style={{ fontSize: '0.95rem' }}>
            {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span><strong>Momentum:</strong></span>
                <div style={{ flex: 1, height: '20px', backgroundColor: '#eee', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${block.momentum || 50}%`, backgroundColor: '#4CAF50' }}></div>
                </div>
                <span>{block.momentum || 50}%</span>
              </div>
            </div>
          </div>
        </div>
      );

    case 'trivia':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>🧠 Trivia Question</h4>
          <div style={{ fontSize: '0.95rem' }}>
            {block.question && <div style={{ marginBottom: '0.75rem', fontWeight: 'bold' }}>{getStringValue(block.question)}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
              {block.optionA && <div>A) {getStringValue(block.optionA)}</div>}
              {block.optionB && <div>B) {getStringValue(block.optionB)}</div>}
              {block.optionC && <div>C) {getStringValue(block.optionC)}</div>}
              {block.optionD && <div>D) {getStringValue(block.optionD)}</div>}
            </div>
            {block.correct && <div><strong>Correct Answer:</strong> {getStringValue(block.correct)}</div>}
            {block.explanation && <div><strong>Explanation:</strong> {getStringValue(block.explanation)}</div>}
          </div>
        </div>
      );

    case 'pickems':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>🏆 Pick 'Em</h4>
          <div style={{ fontSize: '0.95rem' }}>
            {block.team1 && block.team2 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ padding: '0.5rem', backgroundColor: block.pick === 'team1' ? '#e3f2fd' : 'transparent', borderRadius: '4px' }}>
                  {block.pick === 'team1' && '✓ '}{getStringValue(block.team1)}
                </div>
                <div style={{ margin: '0.5rem 0', textAlign: 'center', color: '#999' }}>vs</div>
                <div style={{ padding: '0.5rem', backgroundColor: block.pick === 'team2' ? '#e3f2fd' : 'transparent', borderRadius: '4px' }}>
                  {block.pick === 'team2' && '✓ '}{getStringValue(block.team2)}
                </div>
              </div>
            )}
            {block.confidence && <div><strong>Confidence:</strong> {getStringValue(block.confidence)}</div>}
          </div>
        </div>
      );

    case 'transactions':
      return (
        <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 0.75rem 0' }}>📰 Transaction</h4>
          <div style={{ fontSize: '0.95rem' }}>
            {block.type && <div><strong>Type:</strong> {String(getStringValue(block.type)).charAt(0).toUpperCase() + String(getStringValue(block.type)).slice(1)}</div>}
            {block.player && <div><strong>Player:</strong> {getStringValue(block.player)}</div>}
            {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
            {block.date && <div><strong>Date:</strong> {getStringValue(block.date)}</div>}
          </div>
        </div>
      );

    default:
      return null;
  }
};

const FormattedContentDisplay = ({ content, className = '' }) => {
  let isBlockBased = false;
  let blocks = [];

  try {
    const parsed = JSON.parse(content);
    console.log('[FormattedContentDisplay] Parsed blocks:', parsed);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].type) {
      isBlockBased = true;
      blocks = parsed;
    }
  } catch (e) {
    isBlockBased = false;
  }

  if (isBlockBased) {
    return (
      <div className={`formatted-content ${className}`}>
        {blocks.map(block => renderBlock(block))}
      </div>
    );
  }

  const sanitizedContent = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'strike', 's',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'code', 'pre',
      'a', 'img', 'video', 'source', 'iframe',
      'span', 'div'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'controls', 'type', 'style', 'class', 'allowfullscreen', 'frameborder', 'allow', 'title'],
    ALLOWED_STYLES: {
      '*': {
        'color': [/^#[0-9a-fA-F]{6}$/, /^rgb/, /^hsl/],
        'background-color': [/^#[0-9a-fA-F]{6}$/, /^rgb/, /^hsl/],
        'background': [/^#[0-9a-fA-F]{6}$/, /^rgb/, /^hsl/],
        'font-family': [
          'sans-serif', 'serif', 'monospace',
          'georgia', 'palatino', 'times-new-roman', 'courier-new',
          'arial', 'helvetica', 'verdana', 'trebuchet-ms', 'geneva',
          'comic-sans-ms', 'impact', 'lucida-console',
          'roboto', 'open-sans', 'droid-sans', 'ubuntu',
          'inconsolata', 'mirza', 'aref'
        ],
        'font-size': [/^[\d.]+(?:px|em|rem|pt)$/],
        'font-weight': ['bold', 'normal', /^\d{3}$/],
        'font-style': ['italic', 'normal'],
        'text-decoration': ['underline', 'line-through', 'none'],
        'text-align': ['left', 'center', 'right', 'justify'],
        'width': [/^[\d.]+(?:%|px|em|rem|vw)$/],
        'height': [/^[\d.]+(?:%|px|em|rem|auto|vh)$/],
        'max-width': [/^[\d.]+(?:%|px|em|rem|vw)$/],
        'display': ['block', 'inline', 'inline-block'],
        'margin': [/^[\d.]+(?:px|em|rem|%|\s)+$/],
        'padding': [/^[\d.]+(?:px|em|rem|%|\s)+$/],
        'border-radius': [/^[\d.]+(?:px|em|rem|%)$/],
        'object-fit': ['cover', 'contain', 'fill', 'scale-down']
      }
    },
    KEEP_CONTENT: true
  });

  return (
    <div
      className={`formatted-content ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

export default FormattedContentDisplay;
